import React, { useState, useEffect } from 'react';
import { getLocations, createLocation, updateLocation, deleteLocation } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    MapPin,
    Plus,
    Pencil,
    Trash,
    Buildings,
    Phone,
    Star,
    Crosshair,
    SpinnerGap
} from '@phosphor-icons/react';
import useGeolocation from '../hooks/useGeolocation';

const LocationsPage = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        is_headquarters: false,
        is_active: true,
        latitude: '',
        longitude: '',
        radius_meters: 100,
    });
    const [detectingGps, setDetectingGps] = useState(false);
    const geo = useGeolocation();

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await getLocations();
            setLocations(response.data);
        } catch (error) {
            toast.error('Error al cargar ubicaciones');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Coerce blank lat/lon to null (backend expects null OR a valid number)
        const parsedLat = formData.latitude === '' || formData.latitude === null
            ? null : parseFloat(formData.latitude);
        const parsedLon = formData.longitude === '' || formData.longitude === null
            ? null : parseFloat(formData.longitude);
        if ((parsedLat !== null && Number.isNaN(parsedLat)) ||
            (parsedLon !== null && Number.isNaN(parsedLon))) {
            toast.error('Latitud/longitud inválidas');
            return;
        }
        // Both must be set together (or both empty)
        if ((parsedLat === null) !== (parsedLon === null)) {
            toast.error('Debes ingresar tanto latitud como longitud (o dejar ambas vacías)');
            return;
        }
        const parsedRadius = parseInt(formData.radius_meters, 10);
        const payload = {
            ...formData,
            latitude: parsedLat,
            longitude: parsedLon,
            radius_meters: Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : 100,
        };
        try {
            if (editingLocation) {
                await updateLocation(editingLocation.id, payload);
                toast.success('Ubicación actualizada');
            } else {
                await createLocation(payload);
                toast.success('Ubicación creada');
            }
            setShowModal(false);
            resetForm();
            fetchLocations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar');
        }
    };

    const handleEdit = (location) => {
        setEditingLocation(location);
        setFormData({
            name: location.name,
            address: location.address || '',
            phone: location.phone || '',
            is_headquarters: location.is_headquarters,
            is_active: location.is_active,
            latitude: location.latitude != null ? String(location.latitude) : '',
            longitude: location.longitude != null ? String(location.longitude) : '',
            radius_meters: location.radius_meters ?? 100,
        });
        setShowModal(true);
    };

    const handleDelete = async (locationId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta ubicación?')) return;
        try {
            await deleteLocation(locationId);
            toast.success('Ubicación eliminada');
            fetchLocations();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const resetForm = () => {
        setEditingLocation(null);
        setFormData({
            name: '',
            address: '',
            phone: '',
            is_headquarters: false,
            is_active: true,
            latitude: '',
            longitude: '',
            radius_meters: 100,
        });
    };

    const useCurrentGpsAsAnchor = async () => {
        setDetectingGps(true);
        try {
            const coords = await geo.request({ highAccuracy: true, timeout: 8000 });
            setFormData((f) => ({
                ...f,
                latitude: coords.latitude.toFixed(6),
                longitude: coords.longitude.toFixed(6),
            }));
            toast.success(`Coordenadas obtenidas (±${Math.round(coords.accuracy_m)} m)`);
        } catch (err) {
            const msg = err?.code === 'permission_denied'
                ? 'Permiso de ubicación denegado'
                : err?.code === 'unsupported'
                    ? 'Tu navegador no soporta GPS'
                    : err?.message || 'No se pudo obtener la ubicación';
            toast.error(msg);
        } finally {
            setDetectingGps(false);
        }
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="locations-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Ubicaciones
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        {locations.length} puntos de venta registrados
                    </p>
                </div>
                
                <Button
                    onClick={openCreateModal}
                    data-testid="create-location-btn"
                    className="h-12 px-6 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                >
                    <Plus size={18} className="mr-2" />
                    Nueva Ubicación
                </Button>
            </div>

            {/* Locations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.length === 0 ? (
                    <Card className="col-span-full bg-white border-[#ECE3D2] rounded-2xl">
                        <CardContent className="py-12 text-center">
                            <MapPin size={48} weight="duotone" className="mx-auto mb-4 text-[#4B5878] opacity-50" />
                            <p className="text-[#4B5878]">No hay ubicaciones registradas</p>
                            <Button
                                onClick={openCreateModal}
                                className="mt-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                            >
                                Crear Primera Ubicación
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    locations.map((location) => (
                        <Card 
                            key={location.id} 
                            className="bg-white border-[#ECE3D2] rounded-2xl card-hover overflow-hidden"
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                            location.is_headquarters 
                                                ? 'bg-[#FB7185]/10 border border-[#1F2D4B]/20' 
                                                : 'bg-[#F8F4EC] border border-[#ECE3D2]'
                                        }`}>
                                            <Buildings size={24} weight="duotone" className={
                                                location.is_headquarters ? 'text-[#FB7185]' : 'text-[#1F2D4B]'
                                            } />
                                        </div>
                                        <div>
                                            <h3 className="font-editorial font-medium text-[#1F2D4B]">
                                                {location.name}
                                            </h3>
                                            {location.is_headquarters && (
                                                <Badge className="mt-1 bg-[#FB7185]/10 text-[#FB7185] border border-[#1F2D4B]/20">
                                                    <Star size={12} weight="fill" className="mr-1" />
                                                    Matriz
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <Badge className={location.is_active 
                                        ? 'bg-[#D1FAE5] text-[#059669] border border-[#059669]/20' 
                                        : 'bg-red-50 text-[#DC2626] border border-[#DC2626]/20'
                                    }>
                                        {location.is_active ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                </div>

                                <div className="space-y-2 text-sm">
                                    {location.address && (
                                        <div className="flex items-start gap-2 text-[#4B5878]">
                                            <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                                            <span>{location.address}</span>
                                        </div>
                                    )}
                                    {location.phone && (
                                        <div className="flex items-center gap-2 text-[#4B5878]">
                                            <Phone size={16} />
                                            <span>{location.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-2 mt-4 pt-4 border-t border-[#ECE3D2]">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleEdit(location)}
                                        data-testid={`edit-location-${location.id}`}
                                        className="flex-1 h-10 rounded-xl border-[#ECE3D2] hover:bg-[#F8F4EC]"
                                    >
                                        <Pencil size={16} className="mr-2" />
                                        Editar
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDelete(location.id)}
                                        data-testid={`delete-location-${location.id}`}
                                        className="h-10 px-3 rounded-xl border-[#ECE3D2] text-[#DC2626] hover:bg-red-50"
                                    >
                                        <Trash size={16} />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B]">
                            {editingLocation ? 'Editar Ubicación' : 'Nueva Ubicación'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingLocation ? 'Modifica los datos de la ubicación' : 'Ingresa los datos del nuevo punto de venta'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nombre *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Ej: Restaurante La Palapa"
                                data-testid="location-name-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="address">Dirección</Label>
                            <Input
                                id="address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="Calle, número, colonia..."
                                data-testid="location-address-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="phone">Teléfono</Label>
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="+52 555 123 4567"
                                data-testid="location-phone-input"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>

                        {/* GPS anchor — enables auto-detection for promotoras */}
                        <div className="space-y-3 p-4 bg-[#EEF6FF] rounded-xl border border-[#3B82F6]/20">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <Label className="text-sm font-semibold text-[#1E40AF] flex items-center gap-1.5">
                                        <Crosshair size={14} weight="bold" /> Anclaje GPS (auto-detección)
                                    </Label>
                                    <p className="text-[11px] text-[#1E40AF]/70 mt-0.5">
                                        Cuando una promotora entre desde su celular, se auto-seleccionará este punto si está dentro del radio.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={useCurrentGpsAsAnchor}
                                    disabled={detectingGps}
                                    data-testid="use-current-gps-btn"
                                    className="shrink-0 h-9 px-3 rounded-lg border-[#3B82F6]/40 bg-white text-[#1E40AF] hover:bg-[#DBEAFE] text-xs"
                                >
                                    {detectingGps
                                        ? <SpinnerGap size={14} className="animate-spin mr-1" />
                                        : <Crosshair size={14} weight="bold" className="mr-1" />}
                                    {detectingGps ? 'Detectando…' : 'Usar mi ubicación'}
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label htmlFor="latitude" className="text-[11px] text-[#1E40AF]/70">Latitud</Label>
                                    <Input
                                        id="latitude"
                                        type="text"
                                        inputMode="decimal"
                                        value={formData.latitude}
                                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                                        placeholder="21.161908"
                                        data-testid="location-latitude-input"
                                        className="h-10 rounded-lg border-[#3B82F6]/30 bg-white text-xs font-mono"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="longitude" className="text-[11px] text-[#1E40AF]/70">Longitud</Label>
                                    <Input
                                        id="longitude"
                                        type="text"
                                        inputMode="decimal"
                                        value={formData.longitude}
                                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                                        placeholder="-86.851528"
                                        data-testid="location-longitude-input"
                                        className="h-10 rounded-lg border-[#3B82F6]/30 bg-white text-xs font-mono"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="radius_meters" className="text-[11px] text-[#1E40AF]/70">
                                    Radio (metros) — un aeropuerto ≈ 500 m, un kiosco ≈ 30 m
                                </Label>
                                <Input
                                    id="radius_meters"
                                    type="number"
                                    min="10"
                                    max="5000"
                                    step="10"
                                    value={formData.radius_meters}
                                    onChange={(e) => setFormData({ ...formData, radius_meters: e.target.value })}
                                    data-testid="location-radius-input"
                                    className="h-10 rounded-lg border-[#3B82F6]/30 bg-white text-xs"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-[#F8F4EC] rounded-xl">
                            <div>
                                <Label htmlFor="is_headquarters">Oficina Matriz</Label>
                                <p className="text-xs text-[#4B5878] mt-1">Marcar como sede principal</p>
                            </div>
                            <Switch
                                id="is_headquarters"
                                checked={formData.is_headquarters}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_headquarters: checked })}
                                data-testid="location-hq-switch"
                            />
                        </div>
                        
                        {editingLocation && (
                            <div className="flex items-center justify-between p-4 bg-[#F8F4EC] rounded-xl">
                                <Label htmlFor="is_active">Ubicación Activa</Label>
                                <Switch
                                    id="is_active"
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    data-testid="location-active-switch"
                                />
                            </div>
                        )}
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowModal(false)}
                                className="flex-1 h-12 rounded-xl border-[#ECE3D2]"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                data-testid="save-location-btn"
                                className="flex-1 h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                            >
                                {editingLocation ? 'Guardar' : 'Crear'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LocationsPage;
